import { useEffect, useMemo, useState } from "react"

import { Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import EmptyComponent from "../../../Components/Empty"
import SelectComponent from "../../../Components/Select"
import { RoutesPaths } from "../../../constants/user-routes"
import Sankey, { roundPct } from "../../DashboardPage/components/Sankey"
import { clearFilters, setProp, setSelectedProgram } from "../../ReceiptPage/redux/receipt.slice"
import { DEFLECT_SECTION } from "../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFilters,
  selectDeflectFirstLoad,
  selectDeflectSection
} from "../redux/deflectAnalytics.slice"
import { rangeOfPeriod } from "../utils/records"
import { CHANNEL_COLORS, channelLabel, programLabel } from "../utils/vocabulary"
import LookupFlowSkeleton from "./LookupFlowSkeleton"
import Section from "./Section"

// Every node its own hue, so a ribbon can be traced end to end; channels as on the donut.
// The root's navy sinks into the dark card, so it is lifted there.
const ROOT_COLOR = { light: "#072F94", dark: "#1448CB" }
const PROGRAM_COLORS = { cc: "#1448CB", oi: "#7B2FD6" }
const MATCHED_COLOR = "#1E4FD0"
const NO_MATCH_COLOR = "#DF7009"

// The mobile select's "every program" option; not a program code, so it can't collide with one.
const ALL_LOOKUPS = "__all__"

// All lookups -> program -> channel -> Matched / No match, laid out from flow/'s counts per
// program and channel. A channel node gathers both programs, so its outcome links are summed.
// `drill` is the Receipt dashboard filter a click opens with. That filter bar has no Result,
// so Matched / No match open on the window alone.
const buildFlow = (rows, t, theme) => {
  const nodes = []
  const links = []
  const indexOf = new Map()
  const addNode = (id, name, color, volume, drill) => {
    indexOf.set(id, nodes.length)
    nodes.push({ segment: id, name, volume, color, drill, clickable: Boolean(drill) })
  }
  const link = (source, target, value, volume) => {
    if (value > 0)
      links.push({ source: indexOf.get(source), target: indexOf.get(target), value, volume })
  }
  const sum = (list, field) => list.reduce((total, row) => total + (row[field] || 0), 0)

  const programs = [...new Set(rows.map((row) => row.program))]
  const channels = [...new Set(rows.map((row) => row.channel))]
  const split = rows.map((row) => ({
    ...row,
    unmatched_count: row.lookups_count - row.matched_count,
    unmatched_fees_amount: row.fees_amount - row.matched_fees_amount
  }))

  addNode("root", t("All lookups"), ROOT_COLOR[theme], sum(rows, "fees_amount"), {})
  programs.forEach((program) => {
    const ofProgram = rows.filter((row) => row.program === program)
    addNode(
      `p_${program}`,
      programLabel(program),
      PROGRAM_COLORS[program],
      sum(ofProgram, "fees_amount"),
      { program: [program] }
    )
  })
  channels.forEach((channel) => {
    const ofChannel = rows.filter((row) => row.channel === channel)
    addNode(
      `c_${channel}`,
      channelLabel(channel),
      CHANNEL_COLORS[channel],
      sum(ofChannel, "fees_amount"),
      { channel: [channel] }
    )
  })
  addNode("matched", t("Matched"), MATCHED_COLOR, sum(split, "matched_fees_amount"), {})
  addNode("no_match", t("No match"), NO_MATCH_COLOR, sum(split, "unmatched_fees_amount"), {})

  programs.forEach((program) => {
    const ofProgram = rows.filter((row) => row.program === program)
    link("root", `p_${program}`, sum(ofProgram, "lookups_count"), sum(ofProgram, "fees_amount"))
  })
  rows.forEach((row) =>
    link(`p_${row.program}`, `c_${row.channel}`, row.lookups_count, row.fees_amount)
  )
  channels.forEach((channel) => {
    const ofChannel = split.filter((row) => row.channel === channel)
    link(
      `c_${channel}`,
      "matched",
      sum(ofChannel, "matched_count"),
      sum(ofChannel, "matched_fees_amount")
    )
    link(
      `c_${channel}`,
      "no_match",
      sum(ofChannel, "unmatched_count"),
      sum(ofChannel, "unmatched_fees_amount")
    )
  })

  return dropUnused(nodes, links)
}

// Mobile has no room for four columns side by side, so — like Prevent's Alerts flow — a select
// picks All lookups or one program, and only its own Matched / No match split is drawn.
const buildMobileFlow = (rows, selected, t, theme) => {
  const nodes = []
  const links = []
  const sum = (list, field) => list.reduce((total, row) => total + (row[field] || 0), 0)
  const all = selected === ALL_LOOKUPS
  const picked = all ? rows : rows.filter((row) => row.program === selected)
  const programDrill = all ? {} : { program: [selected] }
  const count = sum(picked, "lookups_count")
  const matched = sum(picked, "matched_count")
  const matchedFees = sum(picked, "matched_fees_amount")
  const fees = sum(picked, "fees_amount")

  nodes.push({
    segment: all ? "root" : `p_${selected}`,
    name: all ? t("All lookups") : programLabel(selected),
    volume: fees,
    color: all ? ROOT_COLOR[theme] : PROGRAM_COLORS[selected],
    // The program is this chart's own root, so left alone it would read 100% of itself.
    pctOverride: all ? undefined : roundPct(count, sum(rows, "lookups_count")),
    drill: programDrill,
    clickable: true
  })
  nodes.push({
    segment: "matched",
    name: t("Matched"),
    volume: matchedFees,
    color: MATCHED_COLOR,
    drill: { ...programDrill, result: ["matched"] },
    clickable: true
  })
  nodes.push({
    segment: "no_match",
    name: t("No match"),
    volume: fees - matchedFees,
    color: NO_MATCH_COLOR,
    drill: { ...programDrill, result: ["not_matched", "failed"] },
    clickable: true
  })
  if (matched > 0) links.push({ source: 0, target: 1, value: matched, volume: matchedFees })
  if (count - matched > 0)
    links.push({ source: 0, target: 2, value: count - matched, volume: fees - matchedFees })

  return dropUnused(nodes, links)
}

// A node nothing flows into or out of (No match on a perfect window) is left out, and the
// links are re-indexed onto what remains.
const dropUnused = (nodes, links) => {
  const used = new Set(links.flatMap((l) => [l.source, l.target]))
  const kept = nodes.map((node, i) => ({ node, i })).filter(({ i }) => used.has(i))
  const newIndex = new Map(kept.map(({ i }, j) => [i, j]))
  return {
    nodes: kept.map(({ node }) => node),
    links: links.map((l) => ({
      ...l,
      source: newIndex.get(l.source),
      target: newIndex.get(l.target)
    }))
  }
}

// "Lookup flow": how bank inquiries flow from intake through the program and channel they
// arrived on to a final result. The root equals the window's lookup count; none is dropped.
const LookupFlow = ({ getData }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { period, program: programFilter } = useSelector(selectDeflectFilters)
  const theme = useSelector((s) => s.nav.theme)
  const isMobile = useSelector((s) => s.nav.isMobile)
  const flow = useSelector(selectDeflectSection(DEFLECT_SECTION.FLOW))
  const fetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.FLOW))
  const firstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.FLOW))

  useEffect(() => {
    getData(DEFLECT_SECTION.FLOW)
  }, [getData])

  const rows = useMemo(() => flow?.result ?? [], [flow])
  const programs = useMemo(() => [...new Set(rows.map((row) => row.program))], [rows])
  const [selected, setSelected] = useState(ALL_LOOKUPS)
  // A program the window or the program filter no longer has falls back to All lookups.
  const shown = programs.includes(selected) ? selected : ALL_LOOKUPS

  const data = useMemo(() => {
    const tone = theme === "dark" ? "dark" : "light"
    return isMobile ? buildMobileFlow(rows, shown, t, tone) : buildFlow(rows, t, tone)
  }, [rows, shown, isMobile, t, theme])

  // The Receipt dashboard opens on the node, the Analytics window and its program, with nothing
  // left over from an earlier visit. Its API takes one program, so both programs read as "all".
  const openLookups = ({ drill }) => {
    dispatch(clearFilters())
    dispatch(setSelectedProgram("all"))
    dispatch(
      setProp({
        key: "filters",
        value: {
          date: rangeOfPeriod(period),
          program: drill.program ?? (programFilter.length === 1 ? programFilter : "all"),
          channel: drill.channel ?? "all"
        }
      })
    )
    navigate(RoutesPaths.receipts.all)
  }

  return (
    <Section
      title={t("Lookup flow")}
      tooltip={t(
        "How bank inquiries flow from intake through the program and channel they arrived on to a final result. Root equals the window's lookup count; no lookup is dropped."
      )}
    >
      {firstLoad ? (
        <LookupFlowSkeleton />
      ) : (
        // "visible": the global spin wrapper clips its content, and the Sankey's tooltip rises above
        // the chart's top edge.
        <Spin spinning={fetching} wrapperClassName="visible">
          {isMobile && rows.length > 0 && (
            <SelectComponent
              size="large"
              value={shown}
              onChange={setSelected}
              options={[
                { label: t("All lookups"), value: ALL_LOOKUPS },
                ...programs.map((program) => ({ label: programLabel(program), value: program }))
              ]}
              style={{ width: "100%", marginBottom: 16 }}
            />
          )}
          {data.links.length ? (
            <Sankey
              data={data}
              theme={theme}
              isMobile={isMobile}
              unit={t("lookups")}
              onNodeClick={openLookups}
            />
          ) : (
            <EmptyComponent type="no_data_available" />
          )}
        </Spin>
      )}
    </Section>
  )
}

export default LookupFlow
